import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Checkbox,
    IconButton,
    List,
    ListItem,
    ListItemText,
    InputAdornment,
    Paper,
    Autocomplete,
} from "@mui/material";
import {
    Close as CloseIcon,
    Search as SearchIcon,
    DeleteOutline as DeleteIcon,
    Place as PlaceIcon,
    Apartment as CompanyIcon,
    BrandingWatermark as BrandIcon,
    Map as MapIcon,
    ReceiptLong as TermsIcon,
    Badge as TaxIdIcon,
    Lock as CreditHoldIcon,
    Category as BrandGroupIcon,
} from "@mui/icons-material";
import { SAMPLE_CUSTOMERS } from "./test-data";

const UserCustomers = ({
    options = [],
    initialCustomers = [],
    onAdd,
    onRemove,
}) => {
    // Normalize incoming initial values to objects
    const normalizeToObject = (val) =>
        typeof val === "string" ? { name: val } : val || {};

    const [customers, setCustomers] = useState(
        Array.isArray(initialCustomers)
            ? initialCustomers.map(normalizeToObject)
            : []
    );
    const [inputValue, setInputValue] = useState("");
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Helpers to identify/label options across mixed shapes from data sources
    const getOptionId = (o) =>
        (o &&
            (o.id ||
                o.customerId ||
                o.customerNumber ||
                o.accountNumber ||
                o.ExternalId ||
                o.ExternalID ||
                o.Id)) ||
        (o && (o.name || o.customerName || o.CustomerName || o.Name)) ||
        JSON.stringify(o);
    const toNA = (v) => {
        if (v === null || v === undefined) return "N/A";
        const s = String(v);
        return s.trim() === "" ? "N/A" : s;
    };

    const getOptionLabel = (o) => {
        if (!o) return "";
        if (typeof o === "string") return o;
        const id = toNA(
            o.id || o.customerNumber || o.customerId || o.accountNumber || o.Id
        );
        const name = toNA(o.name || o.customerName || o.CustomerName || o.Name);
        return `${id} – ${name}`;
    };

    const extractFields = (o) => {
        const id = toNA(
            o.id || o.customerId || o.customerNumber || o.accountNumber || o.Id
        );
        const name = toNA(o.name || o.customerName || o.CustomerName || o.Name);
        const address1 = toNA(
            o.addressLineOne || o.Address_LineOne || o.Address1 || o.address1
        );
        const address2 = toNA(
            o.addressLineTwo || o.Address_LineTwo || o.Address2 || o.address2
        );
        const address3 = toNA(
            o.addressLineThree || o.Address_LineThree || o.Address3 || o.address3
        );
        const address4 = toNA(
            o.addressLineFour || o.Address_LineFour || o.Address4 || o.address4
        );
        const city = toNA(
            o.address_City || o.Address_City || o.city || o.City || o.addressCity
        );
        const state = toNA(
            o.address_State || o.Address_State || o.state || o.State || o.addressState
        );
        const postal = toNA(
            o.address_PostalCode ||
            o.Address_PostalCode ||
            o.postalCode ||
            o.zip ||
            o.Zip
        );
        const country = toNA(
            o.address_Country || o.Address_Country || o.country || o.Country
        );
        const companyCode = toNA(o.companyCode || o.CompanyCode);
        const taxId = toNA(o.taxId || o.TaxId || o.TaxId1);
        const brandCode = toNA(o.brandCode || o.BrandCode);
        const territory = toNA(o.salesTerritoryCode || o.SalesTerritoryCode);
        const creditHold = toNA(o.creditHold ?? o.CreditHold);
        const brandCodeGroup = toNA(o.brandCodeGroup || o.BrandCodeGroup);
        const paymentTerms = toNA(o.paymentTerms || o.PaymentTerms);
        return {
            id,
            name,
            address1,
            address2,
            address3,
            address4,
            city,
            state,
            postal,
            country,
            companyCode,
            taxId,
            brandCode,
            territory,
            creditHold,
            brandCodeGroup,
            paymentTerms,
        };
    };

    const addCustomer = (option) => {
        if (!option) return;
        const exists = customers.some(
            (c) => getOptionId(c) === getOptionId(option)
        );
        if (exists) {
            setSelectedOption(null);
            setInputValue("");
            return;
        }
        const next = [...customers, option];
        setCustomers(next);
        setSelectedOption(null);
        setInputValue("");
        if (onAdd) onAdd(option, next);
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleBulkRemove = () => {
        if (selectedIds.size === 0) return;
        const idsToRemove = new Set(selectedIds);
        const removed = customers.filter((c) => idsToRemove.has(getOptionId(c)));
        const next = customers.filter((c) => !idsToRemove.has(getOptionId(c)));
        setCustomers(next);
        setSelectedIds(new Set());
        if (onRemove) removed.forEach((r) => onRemove(r, next));
    };

    const clearSearch = () => {
        setInputValue("");
        setSelectedOption(null);
    };

    return (
        <Paper
            elevation={0}
            sx={{ p: 2, height: "100%", position: "relative", pb: 8 }}
        >
            <Box
                sx={{
                    display: "flex",
                    gap: { xs: 1, md: 2 },
                    mb: 2,
                    width: "100%",
                    flexDirection: { xs: "column", lg: "row" },
                    alignItems: { md: "center" },
                }}
            >
                <Autocomplete
                    disablePortal
                    options={options && options.length ? options : SAMPLE_CUSTOMERS}
                    value={selectedOption}
                    inputValue={inputValue}
                    onInputChange={(_, v) => setInputValue(v)}
                    onChange={(_, v) => addCustomer(v)}
                    getOptionLabel={getOptionLabel}
                    renderOption={(props, option) => (
                        <ListItem
                            {...props}
                            style={{ fontSize: "1.25rem", paddingTop: 10, paddingBottom: 10 }}
                        >
                            <ListItemText primary={getOptionLabel(option)} />
                        </ListItem>
                    )}
                    isOptionEqualToValue={(o, v) => getOptionId(o) === getOptionId(v)}
                    sx={{ width: { xs: "100%", lg: 380 } }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search customers by number or name"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    height: "40px",
                                    fontSize: "14px",
                                },
                                "& .MuiInputBase-input": {
                                    fontSize: "1.25rem",
                                    height: "40px",
                                },
                            }}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: "#666", fontSize: "20px" }} />
                                            </InputAdornment>
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                    endAdornment: (
                                        <>
                                            {inputValue ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={clearSearch}
                                                        sx={{ color: "#666" }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: "18px" }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                },
                            }}
                        />
                    )}
                />
            </Box>

            <Typography
                variant="subtitle1"
                sx={{ mb: 1, color: "#666", fontSize: "1.3rem" }}
            >
                Customers:
            </Typography>

            {customers.length === 0 ? (
                <Typography color="text.secondary">No customers added.</Typography>
            ) : (
                <List sx={{ bgcolor: "transparent" }}>
                    {customers.map((customer) => {
                        const id = getOptionId(customer);
                        const label = getOptionLabel(customer);
                        const parsedFields = extractFields(customer);
                        return (
                            <ListItem key={id} divider sx={{ pl: 0 }}>
                                <Checkbox
                                    edge="start"
                                    checked={selectedIds.has(id)}
                                    onChange={() => toggleSelect(id)}
                                    sx={{ mr: 1, "& .MuiSvgIcon-root": { fontSize: 20 } }}
                                />

                                <ListItemText
                                    primary={label}
                                    slotProps={{
                                        primary: {
                                            fontSize: "1.35rem",
                                            id: `customer-${id}`,
                                        },
                                    }}
                                    secondary={
                                        <Box sx={{ mt: 0.5 }}>
                                            {parsedFields.address1 && (
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <PlaceIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            mr: 1,
                                                            color: "text.secondary",
                                                        }}
                                                    />

                                                    <Typography
                                                        variant="body1"
                                                        color="text.secondary"
                                                        sx={{ fontSize: "1.1rem" }}
                                                    >
                                                        {parsedFields.address1}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {parsedFields.address2 && (
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{ fontSize: "1.1rem", ml: 3 }}
                                                >
                                                    {parsedFields.address2}
                                                </Typography>
                                            )}
                                            {parsedFields.address3 && (
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{ fontSize: "1.1rem", ml: 3 }}
                                                >
                                                    {parsedFields.address3}
                                                </Typography>
                                            )}
                                            {parsedFields.address4 && (
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{ fontSize: "1.1rem", ml: 3 }}
                                                >
                                                    {parsedFields.address4}
                                                </Typography>
                                            )}

                                            {[
                                                parsedFields.city,
                                                parsedFields.state,
                                                parsedFields.postal,
                                            ].some(Boolean) && (
                                                    <Typography
                                                        variant="body1"
                                                        color="text.secondary"
                                                        sx={{ fontSize: "1.1rem", ml: 3 }}
                                                    >
                                                        {[
                                                            parsedFields.city,
                                                            parsedFields.state,
                                                            parsedFields.postal,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(", ")}
                                                    </Typography>
                                                )}

                                            {parsedFields.country && (
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{ fontSize: "1.1rem", ml: 3 }}
                                                >
                                                    {parsedFields.country}
                                                </Typography>
                                            )}

                                            {[
                                                parsedFields.companyCode,
                                                parsedFields.brandCode,
                                                parsedFields.territory,
                                                parsedFields.paymentTerms,
                                                parsedFields.taxId,
                                                parsedFields.creditHold,
                                                parsedFields.brandCodeGroup,
                                            ].some(Boolean) && (
                                                    <Box
                                                        sx={{
                                                            mt: 0.5,
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 2,
                                                            ml: 0.5,
                                                        }}
                                                    >
                                                        {parsedFields.companyCode && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontSize: "1rem",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <CompanyIcon sx={{ fontSize: 18, mr: 0.5 }} />{" "}
                                                                Company: {parsedFields.companyCode}
                                                            </Typography>
                                                        )}

                                                        {parsedFields.brandCode && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontSize: "1rem",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <BrandIcon sx={{ fontSize: 18, mr: 0.5 }} />{" "}
                                                                BrandCode: {parsedFields.brandCode}
                                                            </Typography>
                                                        )}

                                                        {parsedFields.territory && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontSize: "1rem",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <MapIcon sx={{ fontSize: 18, mr: 0.5 }} />{" "}
                                                                Territory: {parsedFields.territory}
                                                            </Typography>
                                                        )}

                                                        {parsedFields.paymentTerms && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontSize: "1rem",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <TermsIcon sx={{ fontSize: 18, mr: 0.5 }} />{" "}
                                                                Terms: {parsedFields.paymentTerms}
                                                            </Typography>
                                                        )}

                                                        {parsedFields.taxId && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontSize: "1rem",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <TaxIdIcon sx={{ fontSize: 18, mr: 0.5 }} />{" "}
                                                                TaxId: {parsedFields.taxId}
                                                            </Typography>
                                                        )}

                                                        {parsedFields.creditHold && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontSize: "1rem",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <CreditHoldIcon sx={{ fontSize: 18, mr: 0.5 }} />{" "}
                                                                CreditHold: {parsedFields.creditHold}
                                                            </Typography>
                                                        )}

                                                        {parsedFields.brandCodeGroup && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontSize: "1rem",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <BrandGroupIcon sx={{ fontSize: 18, mr: 0.5 }} />{" "}
                                                                BrandGroup: {parsedFields.brandCodeGroup}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                        </Box>
                                    }
                                />
                            </ListItem>
                        );
                    })}
                </List>
            )}

            <Box sx={{ position: "absolute", right: 16, bottom: 16 }}>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleBulkRemove}
                    startIcon={<DeleteIcon />}
                    size="medium"
                    disabled={selectedIds.size === 0}
                    sx={{ fontSize: "1.25rem", width: { xs: "100%", lg: "auto" } }}
                >
                    {`Remove Selected (${selectedIds.size})`}
                </Button>
            </Box>
        </Paper>
    );
};

export default UserCustomers;
